import React from 'react';
import './ChatMessage.css';

const ChatMessage = ({ message }) => {
  const formatTimestamp = (timestamp) => {
    if (timestamp) {
      // timestamp is now an integer (Unix timestamp)
      const date = new Date(timestamp * 1000);
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit',
        hour12: false 
      });
    }
    // Fallback to current time if no timestamp
    const now = new Date();
    return now.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      hour12: false 
    });
  };

  const getChannelName = (channelId) => {
    // Map channel IDs to names (channel is now an integer)
    const channelMap = {
      0: '',
      1: 'Shout',
      2: 'Trade',
      3: 'PartySearch',
      4: 'Party',
      5: 'Raid',
      6: 'Nation',
      7: 'Guild',
      18: 'Global',
    };
    return channelMap[channelId] || `Channel ${channelId}`;
  };

  const getChannelColor = (channelId) => {
    // Map channel IDs to colors
    const channelColors = {
      2: '#D4E157',
      6: '#808000', 
      18: '#00FFFF', 
    };
    return channelColors[channelId] || '#ffffff'; // Default white
  };

  const hasSpecialLinks = (linkTypes) => {
    // Check if message has special link types that should show shield icon
    // linkTypes is now an array of integers
    if (linkTypes && linkTypes.length > 0) {
      return linkTypes.some(type => type === 2 || type === 3);
    }
    return false;
  };

  const parseMessage = (text, linkColors = []) => {
    // Color mapping for link colors (array index to hex color)
    const colorMap = {
      0: '#ba976d',
      1: '#ba976d',
      2: '#77b064',
      3: '#558fd6',
      4: '#c16ecc',
      5: '#d68b06',
      6: '#e07853',
      7: '#f85252',
      8: '#cf7d5d',
      9: '#8fa5c9',
      10: '#cf7d5d',
      11: '#c90b0b',
      12: '#ae98fd',
      13: '#22e3ff',
      14: '#22e3ff',
      15: '#e8afff',
      16: '#f99330'
    };

    // Parse colored text patterns like [Item Name] or [Channel-Event]
    const coloredPattern = /\[([^\]]+)\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let colorIndex = 0;

    while ((match = coloredPattern.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.slice(lastIndex, match.index)
        });
      }

      // Add the colored item/channel
      const itemName = match[1];
      let color = '#ff8c00'; // Default orange for items
      
      // Use linkColors array if available, mapping to colorMap
      if (linkColors && linkColors.length > colorIndex) {
        const colorIndexValue = linkColors[colorIndex];
        if (colorMap.hasOwnProperty(colorIndexValue)) {
          color = colorMap[colorIndexValue];
        }
        colorIndex++;
      }

      parts.push({
        type: 'item',
        content: `[${itemName}]`,
        color: color
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex)
      });
    }

    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  const messageParts = parseMessage(message.message, message.linkColors);
  const channelColor = getChannelColor(message.channel);

  return (
    <div className={`chat-message ${hasSpecialLinks(message.linkTypes) ? 'special' : ''}`}>
      <div className="message-header">
        <span className="timestamp">{formatTimestamp(message.timestamp)}</span>
        {hasSpecialLinks(message.linkTypes) && <span className="special-icon">🛡️</span>}
        <span 
          className="channel-player" 
          style={{ color: channelColor }}
        >
          [{getChannelName(message.channel)}: {message.playerName}]:
        </span>
        <span className="message-content" style={{ color: channelColor }}>
          {messageParts.map((part, index) => (
            <span
              key={index}
              className={part.type}
              style={part.color ? { color: part.color } : { color: channelColor }}
            >
              {part.content}
            </span>
          ))}
        </span>
      </div>
    </div>
  );
};

export default ChatMessage;
